import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Loader2, X, Plus, ArrowRight, Trash2, ListPlus } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import { useToast } from '@/context/ToastContext'
import type { Project, TaskPriority } from '@/lib/types'
import type { ProjectDraft } from '@/components/CreateProjectModal'

// Langkah kedua pembuatan project: daftar task awal, ditulis sendiri oleh pembuatnya.
// Tidak ada jalur AI di sini — rincian project ditentukan orang, bukan tebakan model.

interface DraftTask {
  title: string
  description: string
  priority: TaskPriority
  selected: boolean
}

interface Props {
  draft: ProjectDraft
  onClose: () => void
  onCreated?: (project: Project) => void
}

const PRIORITY_STYLE: Record<TaskPriority, string> = {
  high:   'bg-[#FEF2F2] text-[#DC2626]',
  medium: 'bg-[#FEF3C7] text-[#B45309]',
  low:    'bg-[#F1F5F9] text-[#64748B]',
}

export function TaskRecommendationModal({ draft, onClose, onCreated }: Props) {
  const { createProject, createTask, updateProject, assignPic } = useApp()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [tasks, setTasks] = useState<DraftTask[]>([])
  const [saveError, setSaveError] = useState('')
  const [saving, setSaving] = useState(false)
  // ── Form tambah task ──
  const [nTitle, setNTitle] = useState('')
  const [nDesc, setNDesc] = useState('')
  const [nPriority, setNPriority] = useState<TaskPriority>('medium')

  const addTask = () => {
    if (!nTitle.trim()) return
    setTasks(prev => [
      ...prev,
      { title: nTitle.trim(), description: nDesc.trim(), priority: nPriority, selected: true },
    ])
    setNTitle(''); setNDesc(''); setNPriority('medium')
  }

  const removeTask = (idx: number) =>
    setTasks(prev => prev.filter((_, i) => i !== idx))

  const toggle = (idx: number) =>
    setTasks(prev => prev.map((t, i) => i === idx ? { ...t, selected: !t.selected } : t))

  const toggleAll = () => {
    const allSelected = tasks.every(t => t.selected)
    setTasks(prev => prev.map(t => ({ ...t, selected: !allSelected })))
  }

  const selectedCount = tasks.filter(t => t.selected).length

  // Setelah project dibuat: assign PIC (ticket kalau akun lain) atau set langsung.
  const applyPic = async (projectId: string) => {
    if (draft.picEmail) {
      await assignPic(projectId, draft.picEmail, draft.pic ?? draft.picEmail)
    } else if (draft.pic) {
      await updateProject(projectId, { pic: draft.pic })
    }
  }

  // Buat project (+ task opsional) di langkah akhir.
  // Kalau pembuatnya MEMBER → project 'pending': belum tampil di sistem, task dibuat
  // via POST langsung (tidak masuk state global), lalu diarahkan ke /approvals.
  // Kalau ADMIN → 'approved': alur normal, langsung buka project.
  const finalize = async (withTasks: boolean) => {
    setSaving(true)
    setSaveError('')
    try {
      const project = await createProject(draft.name, draft.description, draft.color, undefined, {
        visibility: draft.visibility, viewerEmails: draft.viewerEmails,
      })
      await applyPic(project.id)
      const pending = project.approvalStatus === 'pending'

      if (withTasks) {
        const selected = tasks.filter(t => t.selected)
        for (const t of selected) {
          if (pending) {
            await fetch(`/api/projects/${project.id}/tasks`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ title: t.title, description: t.description, priority: t.priority }),
            })
          } else {
            await createTask(project.id, t.title, t.description, t.priority)
          }
        }
      }

      onCreated?.(project)
      if (pending) {
        toast('Project diajukan — menunggu persetujuan admin departemenmu.', 'info')
        navigate('/approvals')
      } else {
        navigate(`/projects/${project.id}`)
      }
      onClose()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Gagal membuat project')
    } finally {
      setSaving(false)
    }
  }

  const handleSave = () => finalize(true)
  const handleSkip = () => finalize(false)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fade-in" />

      <div className="relative w-full max-w-[580px] bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 animate-slide-up max-h-[88vh] flex flex-col">

        {/* Header */}
        <div className="px-6 py-5 border-b border-[#F2F1EC] flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 rounded-lg bg-[#F4F3F0] flex items-center justify-center">
                  <ListPlus className="w-4 h-4 text-[#6B6860]" />
                </div>
                <h2 className="font-display text-lg font-bold text-[#111110]">Tambah Task</h2>
              </div>
              <p className="text-xs text-[#8A8A85] font-body leading-relaxed">
                Tambahkan task untuk{' '}
                <span className="font-semibold text-[#6B6860]">"{draft.name}"</span>{' '}
                — opsional, bisa juga diisi nanti di halaman project.
              </p>
            </div>
            <button onClick={onClose} title="Batal — project belum dibuat"
              className="p-2 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#111110] transition-colors flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-3 gap-3">
              <p className="text-[11px] text-[#8A8A85] font-body">
                <span className="font-semibold text-[#1A1916]">{tasks.length} task</span> ditambahkan
              </p>
              {tasks.length > 0 && (
                <button onClick={toggleAll}
                  className="text-[11px] text-[#6B6860] font-body hover:text-[#111110] hover:underline flex-shrink-0">
                  {tasks.every(t => t.selected) ? 'Batalkan semua' : 'Pilih semua'}
                </button>
              )}
            </div>

            {tasks.length === 0 && (
              <p className="text-[11px] text-[#AEADA8] font-body py-2 text-center">
                Belum ada task. Tambahkan di bawah, atau lewati dan langsung buka project.
              </p>
            )}

            {tasks.map((task, idx) => (
              <div key={idx} role="button" tabIndex={0} onClick={() => toggle(idx)}
                className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 text-left cursor-pointer transition-all duration-150 ${
                  task.selected
                    ? 'bg-[#FAFAF8] border-[#D8D7D0] shadow-sm'
                    : 'bg-[#FAFAF8] border-[#E8E7E3] opacity-50 hover:opacity-70'
                }`}
              >
                {/* Checkbox sertakan */}
                <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                  task.selected ? 'bg-[#111110] border-[#111110]' : 'border-[#C8C7BF]'
                }`}>
                  {task.selected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 mb-1">
                    <p className="text-[13px] font-body font-semibold text-[#111110] flex-1 leading-snug">
                      {task.title}
                    </p>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-body font-bold uppercase tracking-wide flex-shrink-0 ${PRIORITY_STYLE[task.priority]}`}>
                      {task.priority}
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); removeTask(idx) }}
                      title="Hapus task ini" aria-label="Hapus task"
                      className="p-1 -m-1 rounded text-[#C8C7BF] hover:text-[#DC2626] transition-colors flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {task.description && (
                    <p className="text-[11px] text-[#8A8A85] font-body leading-relaxed line-clamp-2">
                      {task.description}
                    </p>
                  )}
                </div>
              </div>
            ))}

            {/* Form tambah task */}
            <div className="border border-dashed border-[#D8D7D0] rounded-xl p-3 space-y-2 mt-1">
              <input
                type="text"
                value={nTitle}
                onChange={e => setNTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTask() } }}
                placeholder="Judul task baru…"
                className="w-full text-[13px] bg-[#F8F7F3] border border-[#E0DFD8] rounded-lg px-3 py-2 font-body font-semibold text-[#111110] placeholder:text-[#C8C7BF] placeholder:font-normal focus:outline-none focus:border-[#111110] focus:bg-white transition-all"
              />
              <textarea
                value={nDesc}
                onChange={e => setNDesc(e.target.value)}
                placeholder="Deskripsi (opsional)…"
                rows={2}
                className="w-full text-[12px] bg-[#F8F7F3] border border-[#E0DFD8] rounded-lg px-3 py-2 font-body text-[#111110] placeholder:text-[#C8C7BF] focus:outline-none focus:border-[#111110] focus:bg-white resize-none leading-relaxed transition-all"
              />
              <div className="flex items-center gap-2">
                <select
                  value={nPriority}
                  onChange={e => setNPriority(e.target.value as TaskPriority)}
                  className="text-[12px] bg-[#F8F7F3] border border-[#E0DFD8] rounded-lg px-2.5 py-2 font-body text-[#111110] focus:outline-none focus:border-[#111110] transition-all"
                >
                  <option value="high">Prioritas: High</option>
                  <option value="medium">Prioritas: Medium</option>
                  <option value="low">Prioritas: Low</option>
                </select>
                <button
                  onClick={addTask}
                  disabled={!nTitle.trim()}
                  className="flex items-center gap-1.5 px-3 py-2 bg-[#111110] text-white rounded-lg text-[12px] font-medium font-body hover:bg-[#2A2A28] disabled:opacity-40 disabled:cursor-not-allowed transition-all ml-auto"
                >
                  <Plus className="w-3.5 h-3.5" /> Tambah task
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#F2F1EC] flex-shrink-0">
          {saveError && (
            <div className="mb-3 px-3.5 py-2.5 bg-[#FEF2F2] border border-[#FECACA] rounded-xl text-xs text-[#DC2626] font-body">
              {saveError}
            </div>
          )}
          <div className="flex items-center justify-between">
            <button onClick={handleSkip} disabled={saving}
              className="text-sm font-body text-[#8A8A85] hover:text-[#111110] disabled:opacity-40 transition-colors">
              Lewati, buka project
            </button>
            <button
              onClick={handleSave}
              disabled={saving || selectedCount === 0}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#111110] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2A2A28] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {saving
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : selectedCount === 0
                ? <><ArrowRight className="w-3.5 h-3.5" /> Buka Project</>
                : <><Plus className="w-3.5 h-3.5" /> Tambah {selectedCount} Task</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
